import React, { useEffect } from 'react';
import { WindowFrame } from '@sw4p/ui';
import SwapCard from './components/SwapCard';
import WalletBubble from './components/WalletBubble';
import { useProject } from './hooks/useProject';

const HEX_OR_RGB_RE = /^#[0-9a-fA-F]{3,8}$|^rgb\(/;

function App() {
    const { project, loading, error } = useProject();

    // Inject tenant accent as a CSS custom property at the document root.
    // This drives the accent line, primary button tint, and focus rings
    // within the storefront window. Window chrome always stays flat blue
    // (--xp-titlebar-flat). Status LEDs are never tinted by tenant color.
    // Value is validated before injection: a malformed server-side value
    // (semicolon, closing brace, arbitrary text) would silently break
    // downstream var(--tenant-accent, fallback) chains. Only hex/rgb values
    // that match HEX_OR_RGB_RE are applied; anything else removes the
    // property so the CSS fallback kicks in.
    useEffect(() => {
        const raw = project?.primary_color;
        if (raw && HEX_OR_RGB_RE.test(raw.trim())) {
            document.documentElement.style.setProperty('--tenant-accent', raw.trim());
        } else {
            document.documentElement.style.removeProperty('--tenant-accent');
        }
    }, [project?.primary_color]);

    if (loading) {
        return (
            <div className="xp-desktop flex items-center justify-center">
                <div className="text-center">
                    <img
                        src="/brand/logo-transparent.png"
                        alt="SW4P"
                        style={{
                            width: 54,
                            height: 54,
                            objectFit: 'contain',
                            margin: '0 auto 12px',
                            filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.25))',
                        }}
                    />
                    <div
                        className="mx-auto mb-3"
                        style={{
                            width: 32,
                            height: 32,
                            border: '3px solid rgba(0,70,164,0.2)',
                            borderTopColor: '#0046A4',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                        }}
                    />
                    <p style={{
                        fontFamily: "'Tahoma', 'Trebuchet MS', system-ui, sans-serif",
                        fontSize: 13,
                        color: '#fff',
                        textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                    }}>
                        Loading project...
                    </p>
                </div>
            </div>
        );
    }

    if (error || !project) {
        return (
            <div className="xp-desktop flex items-center justify-center p-4">
                <WindowFrame title="Project Not Found" maxWidth="420px">
                    <div className="p-6 text-center">
                        <p style={{
                            fontFamily: "'Tahoma', 'Trebuchet MS', system-ui, sans-serif",
                            fontSize: 13,
                            color: 'var(--xp-text-ink)',
                            marginBottom: 16,
                        }}>
                            This subdomain is not configured. Check the URL or contact support.
                        </p>
                        <a
                            href="https://console.sw4p.io"
                            style={{
                                fontFamily: "'Tahoma', 'Trebuchet MS', system-ui, sans-serif",
                                fontSize: 13,
                                color: 'var(--xp-titlebar-flat)',
                                textDecoration: 'underline',
                            }}
                        >
                            Create a project at console.sw4p.io
                        </a>
                    </div>
                </WindowFrame>
            </div>
        );
    }

    const windowTitle = project.is_universal
        ? 'Checkout: Universal Swap'
        : `Checkout: ${project.name}`;

    return (
        <div
            className="xp-desktop relative flex min-h-screen items-start justify-center p-3 sm:items-center sm:p-4"
            style={
                project.background_url
                    ? {
                        backgroundImage: `url(${project.background_url}), linear-gradient(170deg, #3A72C4 0%, #5BABE8 55%, #88CDEC 75%, #86B947 88%, #3D6B1A 100%)`,
                        backgroundSize: 'cover, cover',
                        backgroundPosition: 'center, center',
                    }
                    : undefined
            }
        >
            {/* Ambient glow tinted to project.primary_color (subtle bg detail, preserved) */}
            <div
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-15 blur-3xl"
                style={{
                    width: 500,
                    height: 500,
                    backgroundColor: project.primary_color,
                }}
            />

            {/* Main checkout window */}
            <div className="w-full max-w-lg pt-14 pb-20 sm:py-0">
                <WindowFrame
                    title={windowTitle}
                    icon={project.logo_url ?? '/brand/logo-transparent.png'}
                    className="storefront-window"
                    maxWidth="100%"
                >
                    <SwapCard project={project} />
                </WindowFrame>
            </div>

            {/* System-tray wallet bubble */}
            <WalletBubble project={project} />
        </div>
    );
}

export default App;
